<template>
	<div class="allow-tree">
		<div v-for="group in groups" :key="group.key" class="allow-group">
			<div class="group-header">
				<q-checkbox
					dark dense
					:model-value="groupState(group)"
					:label="`${group.label}  (${countSelected(group)}/${group.items.length})`"
					color="orange"
					toggle-indeterminate
					@update:model-value="(val:any) => toggleGroup(group, val)"
				/>
			</div>
			<div class="group-items">
				<div v-for="item in group.items" :key="item.id" class="item-row">
					<q-checkbox
						dark dense
						:model-value="modelValue.includes(item.id)"
						:label="item.name"
						color="green"
						@update:model-value="() => toggleItem(item.id)"
					/>
					<span v-if="item.description" class="item-desc">{{ item.description }}</span>
				</div>
				<div v-if="group.items.length === 0" class="empty-group">none</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { AllowGroup } from '../models/allowList';

const props = defineProps<{ groups: AllowGroup[]; modelValue: string[] }>();
const emit = defineEmits<{ (e: 'update:modelValue', v: string[]): void }>();

const countSelected = (group: AllowGroup) =>
	group.items.filter((i) => props.modelValue.includes(i.id)).length;

// true = all selected, false = none, null = some (indeterminate)
const groupState = (group: AllowGroup): boolean | null => {
	const n = countSelected(group);
	if (n === 0) return false;
	if (n === group.items.length) return true;
	return null;
};

const toggleItem = (id: string) => {
	const set = new Set(props.modelValue);
	if (set.has(id)) set.delete(id);
	else set.add(id);
	emit('update:modelValue', [...set]);
};

const toggleGroup = (group: AllowGroup, val: boolean) => {
	const set = new Set(props.modelValue);
	// q-checkbox with toggle-indeterminate cycles false -> true -> null; treat
	// truthy as "select all", anything else as "clear all" for this group.
	if (val === true) group.items.forEach((i) => set.add(i.id));
	else group.items.forEach((i) => set.delete(i.id));
	emit('update:modelValue', [...set]);
};
</script>

<style scoped>
.allow-tree { display: flex; flex-direction: column; gap: 16px; }
.allow-group {
	border: 1px solid #ff660030;
	border-radius: 6px;
	background: #0a1418;
	padding: 8px 12px;
}
.group-header {
	border-bottom: 1px solid #ff660020;
	margin-bottom: 6px;
	padding-bottom: 4px;
}
.group-header :deep(.q-checkbox__label) { color: #ff6600; font-weight: 600; letter-spacing: 0.5px; }
.group-items { display: flex; flex-direction: column; gap: 2px; padding-left: 8px; }
.item-row { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; }
.item-desc { color: #6c8; font-size: 0.75em; opacity: 0.7; }
.empty-group { color: #555; font-style: italic; font-size: 0.8em; padding: 4px; }
</style>
